/**
 * glør Locale Gallery — tauscht auf der ENGLISCHEN Storefront die Galeriebilder
 * ab Position 2 (Index 1) gegen die Dateien aus dem Produkt-Metafeld
 * custom.bild_englisch. Position 1 (Index 0 = Variantenbild) bleibt IMMER
 * unverändert. Bei Deutsch liefert der Block keine Daten-Insel → sofortiges
 * return, das Feature schläft komplett.
 *
 * Update-sicher & additiv: KEIN Eingriff in die Core-Galerie-Dateien. Es werden
 * nur `src`/`srcset`/`alt` und die Zoom-High-Res-Quelle (data-max-resolution)
 * bestehender <img>-Elemente überschrieben — in ALLEN Darstellungen der Galerie
 * (Slideshow-Slides, Grid, Zoom-Dialog) sowie in den Thumbnails.
 *
 * Robust gegen Varianten-Neuaufbau: Horizon ersetzt bei Variantenwechsel die
 * ganze <media-gallery> (media-gallery.js → replaceWith). Ein MutationObserver
 * auf der Section fängt das ab und wendet den Tausch auf die frische Galerie
 * erneut an. Idempotent: jede Galerie-Instanz wird per data-glr-en-applied
 * markiert; die neue (unmarkierte) Galerie wird neu bespielt, die eigenen
 * Attribut-Änderungen lösen keine Endlosschleife aus.
 */

class GlrLocaleGallery extends HTMLElement {
  connectedCallback() {
    this.data = this.#readData();
    if (!Array.isArray(this.data) || this.data.length === 0) return; // Deutsch / leer

    this.section = this.closest('.shopify-section') || document.body;
    this._raf = 0;

    this.#apply();
    this.#observe();
    this.#listenVariant();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.section && this._pselName && this._onProductSelect) {
      this.section.removeEventListener(this._pselName, this._onProductSelect);
    }
  }

  /** @returns {Array|null} */
  #readData() {
    const el = this.querySelector('script[type="application/json"][data-glr-en-gallery]');
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  /** @returns {Element|null} */
  #findGallery() {
    const scope = this.section instanceof Element ? this.section : document;
    return scope.querySelector('media-gallery') || document.querySelector('media-gallery');
  }

  /**
   * Sammelt alle positions-geordneten Slot-Listen der Galerie.
   * Jede Liste ist unabhängig 0-basiert; Index 0 = Variantenbild (bleibt).
   * @param {Element} gallery
   * @returns {{items: Element[], kind: string}[]}
   */
  #collectLists(gallery) {
    const lists = [];
    const push = (nodeList, kind) => {
      const items = Array.from(nodeList);
      if (items.length) lists.push({ items, kind });
    };

    // Slideshow (Karussell + Mobil-Ansicht des Grids): genau eine Slide-Reihe.
    const slides = gallery.querySelectorAll('slideshow-slide');
    if (slides.length) lists.push({ items: Array.from(slides), kind: 'media' });

    // Grid-Ansicht (Desktop).
    gallery.querySelectorAll('.media-gallery__grid').forEach((ul) => {
      push(ul.querySelectorAll(':scope > li'), 'media');
    });

    // Zoom-Dialog Hauptbilder.
    gallery.querySelectorAll('.dialog-zoomed-gallery').forEach((ul) => {
      push(ul.querySelectorAll(':scope > li'), 'media');
    });

    // Slideshow-Thumbnails (kann Desktop + Mobil = zwei Listen sein).
    gallery.querySelectorAll('.slideshow-controls__thumbnails').forEach((c) => {
      push(c.querySelectorAll(':scope > .slideshow-controls__thumbnail'), 'thumb');
    });

    // Zoom-Dialog Thumbnails.
    gallery.querySelectorAll('.dialog-thumbnails-list').forEach((c) => {
      push(c.querySelectorAll(':scope > .dialog-thumbnails-list__thumbnail'), 'thumb');
    });

    return lists;
  }

  /**
   * @param {Element[]} items
   * @param {string} kind
   */
  #applyList(items, kind) {
    const data = this.data;
    // Nur die Überlappung tauschen: Position 1..N, so weit EN-Einträge reichen.
    const max = Math.min(items.length - 1, data.length);
    for (let i = 1; i <= max; i++) {
      const entry = data[i - 1];
      const item = items[i];
      if (!entry || !item) continue;

      if (kind === 'thumb') {
        this.#swapImg(item.querySelector('img'), entry, true);
      } else {
        const img = item.querySelector('.product-media__image') || item.querySelector('img');
        this.#swapImg(img, entry, false);
        this.#setRatio(item, entry);
      }
    }
  }

  /**
   * @param {Element|null} img
   * @param {object} entry
   * @param {boolean} isThumb
   */
  #swapImg(img, entry, isThumb) {
    if (!img || !entry) return;

    // Video-Einträge: best-effort. Nur wenn eine Bildquelle (Poster) vorhanden
    // ist, wird das <img> getauscht — sonst Slot unangetastet lassen.
    if (entry.type === 'video' && !entry.src) return;
    if (!entry.src && !entry.srcset) return;

    if (entry.srcset) img.setAttribute('srcset', entry.srcset);
    if (entry.src) img.setAttribute('src', entry.src);
    if (!isThumb && entry.full) img.setAttribute('data-max-resolution', entry.full);
    if (typeof entry.alt === 'string' && entry.alt !== '') img.setAttribute('alt', entry.alt);
  }

  /**
   * Aktualisiert das Seitenverhältnis, damit ein abweichend proportioniertes
   * EN-Bild nicht verzerrt/beschnitten wird. Defensiv.
   * @param {Element} item
   * @param {object} entry
   */
  #setRatio(item, entry) {
    const ratio = Number(entry.ratio);
    if (!ratio || !isFinite(ratio)) return;
    try {
      const pm = item.matches?.('.product-media') ? item : item.querySelector('.product-media');
      pm?.style.setProperty('--ratio', String(ratio));
      const cont =
        item.closest?.('.product-media-container') ||
        (item.classList?.contains('product-media-container') ? item : null);
      cont?.style.setProperty('--media-preview-ratio', String(ratio));
    } catch (e) {
      /* noop */
    }
  }

  /** @returns {boolean} true wenn eine (noch unmarkierte) Galerie bespielt wurde. */
  #apply() {
    const gallery = this.#findGallery();
    if (!gallery) return false;
    if (gallery.dataset.glrEnApplied === '1') return false; // idempotent pro Instanz

    for (const list of this.#collectLists(gallery)) {
      this.#applyList(list.items, list.kind);
    }
    gallery.dataset.glrEnApplied = '1';
    return true;
  }

  /** rAF-entprellter Re-Apply; no-op wenn Galerie schon markiert/nicht vorhanden. */
  #scheduleApply() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this.#apply();
    });
  }

  /** MutationObserver: fängt den replaceWith der Galerie bei Variantenwechsel. */
  #observe() {
    const scope = this.section instanceof Element ? this.section : document.body;
    if (typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(() => this.#scheduleApply());
    this.observer.observe(scope, { childList: true, subtree: true });
  }

  /** Zusätzlicher Trigger über das native ProductSelect-Event (belt & suspenders). */
  async #listenVariant() {
    try {
      const { StandardEvents } = await import('@shopify/events');
      if (this.section && StandardEvents?.productSelect) {
        this._pselName = StandardEvents.productSelect;
        this._onProductSelect = () => this.#scheduleApply();
        this.section.addEventListener(this._pselName, this._onProductSelect);
      }
    } catch (e) {
      /* Events nicht verfügbar — MutationObserver genügt. */
    }
  }
}

if (!customElements.get('glr-locale-gallery')) {
  customElements.define('glr-locale-gallery', GlrLocaleGallery);
}
